import { createContext, useContext, useEffect, useState } from 'react'
import { authClient } from '../lib/auth-client'
import { isTestMode } from '../lib/test-mode'
import { getAgentUrl } from '../services/agent-url'

interface User {
  id: string
  email?: string
}

interface Session {
  access_token: string
  user: User
}

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  isAdmin: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, inviteToken: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  // Check admin status by probing /api/admin/users (403 = not admin)
  async function checkAdmin(accessToken: string) {
    try {
      const baseUrl = await getAgentUrl()
      const res = await fetch(`${baseUrl}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      })
      setIsAdmin(res.ok)
    } catch {
      setIsAdmin(false)
    }
  }

  useEffect(() => {
    authClient.getSession().then(async ({ data: { session: s } }) => {
      setSession(s)
      setUser(s?.user ?? null)
      if (s?.access_token) await checkAdmin(s.access_token)
      setLoading(false)
    })

    const { data: { subscription } } = authClient.onAuthStateChange((_event: string, s: Session | null) => {
      setSession(s)
      setUser(s?.user ?? null)
      if (s?.access_token) checkAdmin(s.access_token)
      else setIsAdmin(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await authClient.signInWithPassword({ email: email.trim(), password: password.trim() })
    if (error) return { error: new Error(error.message) }

    // Manually fetch session after sign-in (onAuthStateChange may not fire)
    const { data: { session: s } } = await authClient.getSession()
    if (s) {
      setSession(s)
      setUser(s.user ?? null)
      if (s.access_token) await checkAdmin(s.access_token)
    }

    return { error: null }
  }

  const signUp = async (email: string, password: string, inviteToken: string) => {
    if (isTestMode) {
      const { error } = await authClient.signUp({ email: email.trim(), password: password.trim() })
      if (error) return { error: new Error(error.message) }

      // Sign in after sign-up in test mode too
      await authClient.signInWithPassword({ email: email.trim(), password: password.trim() })
      const { data: { session: s } } = await authClient.getSession()
      if (s) {
        setSession(s)
        setUser(s.user ?? null)
      }
      return { error: null }
    }

    // Production: validate invite token before creating account
    const baseUrl = await getAgentUrl()
    const validateRes = await fetch(`${baseUrl}/api/auth/validate-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: inviteToken, email }),
    })

    const validation = await validateRes.json()
    if (!validation.valid) {
      return { error: new Error(validation.reason || 'Invalid invite code') }
    }

    // Create the account
    const { error } = await authClient.signUp({ email, password })
    if (error) return { error: new Error(error.message) }

    // Sign in immediately to get a session
    const { error: signInErr } = await authClient.signInWithPassword({ email, password })
    if (signInErr) return { error: new Error(signInErr.message) }

    // Fetch session and update state (same fix as signIn)
    const { data: { session: newSession } } = await authClient.getSession()
    if (newSession) {
      setSession(newSession)
      setUser(newSession.user ?? null)
      if (newSession.access_token) await checkAdmin(newSession.access_token)

      // Redeem invite token
      await fetch(`${baseUrl}/api/auth/redeem-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${newSession.access_token}`,
        },
        body: JSON.stringify({ token: inviteToken }),
      })
    }

    return { error: null }
  }

  const signOut = async () => {
    await authClient.signOut()
    setUser(null)
    setSession(null)
    window.location.href = '/'
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
